import { useState } from 'react';
import {
  FOLDER_COLORS,
  folderInTrash,
  folderPath,
  saveFolder,
  type LibraryFolder,
} from '@orbit-mind/domain';
import { useLocale, type useLibraryOrganization } from '@orbit-mind/engine-react';
import type { MapSummary } from '@orbit-mind/offline-engine';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/controls';

export function FolderExplorer({
  organization,
  current,
  onOpen,
  maps,
  query,
  trash,
  synced = false,
}: {
  organization: ReturnType<typeof useLibraryOrganization>;
  current: string | null;
  onOpen: (id: string | null) => void;
  maps: readonly MapSummary[];
  query: string;
  trash: boolean;
  /** Signed in: the organization follows the account to its other devices. */
  synced?: boolean;
}): React.JSX.Element {
  const { text: t } = useLocale();
  const { library, ready, busy, update } = organization;
  const [editing, setEditing] = useState<LibraryFolder | null>(null);
  const folders = library.folders.filter((folder) =>
    trash
      ? folder.trashed
      : !folderInTrash(library, folder.id) &&
        (query
          ? folder.name.toLocaleLowerCase().includes(query.toLocaleLowerCase())
          : folder.parentId === current),
  );
  return (
    <section className="mb-6 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav aria-label="Pastas" className="flex flex-wrap items-center gap-2">
          <Button
            tone="ghost"
            onClick={() => {
              onOpen(null);
            }}
          >
            Home
          </Button>
          {folderPath(library, current).map((folder) => (
            <span key={folder.id}>
              {' '}
              /{' '}
              <button
                className="text-accent p-2"
                onClick={() => {
                  onOpen(folder.id);
                }}
              >
                {folder.name}
              </button>
            </span>
          ))}
        </nav>
        <Button
          disabled={!ready || busy || trash}
          onClick={() => {
            setEditing({
              id: `folder_${crypto.randomUUID()}`,
              name: '',
              color: FOLDER_COLORS[0],
              parentId: current,
              trashed: false,
            });
          }}
        >
          + {t('Folder', 'Pasta')}
        </Button>
      </div>
      <p className="text-ink-faint text-xs">
        {synced
          ? t(
              'Folders, tags and favourites sync with your account.',
              'Pastas, tags e favoritos sincronizados com a sua conta.',
            )
          : t(
              'Folders and tags are organized on this device.',
              'Pastas e tags organizadas neste dispositivo.',
            )}
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {folders.map((folder) => (
          <div
            key={folder.id}
            className="border-edge bg-surface flex items-center gap-3 rounded-xl border p-3"
          >
            <span style={{ color: folder.color }} className="text-3xl" aria-hidden="true">
              ▰
            </span>
            <button
              className="min-w-0 flex-1 text-left"
              onClick={() => {
                if (!trash) onOpen(folder.id);
              }}
              disabled={trash}
            >
              <span className="block truncate font-medium">{folder.name}</span>
              <span className="text-ink-muted text-xs">
                {library.folders.filter((f) => f.parentId === folder.id && !f.trashed).length}{' '}
                {t('folders', 'pastas')} ·{' '}
                {
                  maps.filter(
                    (m) =>
                      m.map.deletedAt === null &&
                      library.placements[m.map.id]?.folderId === folder.id,
                  ).length
                }{' '}
                {t('maps', 'mapas')}
              </span>
            </button>
            {trash ? (
              <Button
                disabled={busy}
                onClick={() => {
                  void update((previous) =>
                    saveFolder(previous, {
                      ...folder,
                      trashed: false,
                      parentId: folderInTrash(library, folder.parentId) ? null : folder.parentId,
                    }),
                  );
                }}
              >
                {t('Restore', 'Restaurar')}
              </Button>
            ) : (
              <Button
                aria-label={`${t('Manage folder', 'Gerenciar pasta')} ${folder.name}`}
                onClick={() => {
                  setEditing(folder);
                }}
              >
                ⋯
              </Button>
            )}
          </div>
        ))}
      </div>
      <Modal
        open={editing !== null}
        onOpenChange={(value) => {
          if (!value) setEditing(null);
        }}
        title={t('Folder', 'Pasta')}
        size="narrow"
      >
        {editing && (
          <form
            className="space-y-4 overflow-y-auto p-5"
            onSubmit={(event) => {
              event.preventDefault();
              void update((previous) => saveFolder(previous, editing)).then((ok) => {
                if (ok) setEditing(null);
              });
            }}
          >
            <label className="block">
              {t('Name', 'Nome')}
              <input
                required
                maxLength={100}
                className="border-edge bg-canvas mt-1 w-full rounded-lg border p-3"
                value={editing.name}
                onChange={(event) => {
                  setEditing({ ...editing, name: event.target.value });
                }}
              />
            </label>
            <label className="block">
              {t('Parent folder', 'Pasta de destino')}
              <select
                className="border-edge bg-canvas mt-1 w-full rounded-lg border p-3"
                value={editing.parentId ?? ''}
                onChange={(event) => {
                  setEditing({ ...editing, parentId: event.target.value || null });
                }}
              >
                <option value="">Home</option>
                {library.folders
                  .filter(
                    (f) =>
                      !folderInTrash(library, f.id) &&
                      !folderPath(library, f.id).some((p) => p.id === editing.id),
                  )
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {folderPath(library, f.id)
                        .map((p) => p.name)
                        .join(' / ')}
                    </option>
                  ))}
              </select>
            </label>
            <div className="flex flex-wrap gap-2">
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`Cor ${color}`}
                  aria-pressed={editing.color === color}
                  style={{ backgroundColor: color }}
                  className={`h-10 w-10 rounded-full border-4 ${editing.color === color ? 'border-ink' : 'border-transparent'}`}
                  onClick={() => {
                    setEditing({ ...editing, color });
                  }}
                />
              ))}
            </div>
            {organization.error && <p role="alert">{organization.error}</p>}
            <div className="flex flex-wrap gap-3">
              <Button type="submit" tone="primary" disabled={busy}>
                {t('Save', 'Salvar')}
              </Button>
              {library.folders.some((f) => f.id === editing.id) && (
                <Button
                  disabled={busy}
                  onClick={() => {
                    void update((previous) =>
                      saveFolder(previous, { ...editing, trashed: true }),
                    ).then((ok) => {
                      if (ok) {
                        setEditing(null);
                        onOpen(null);
                      }
                    });
                  }}
                >
                  {t('Move to trash', 'Mover para a lixeira')}
                </Button>
              )}
            </div>
          </form>
        )}
      </Modal>
    </section>
  );
}
